import { FC, useState, useMemo, useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useTherapistDashboardStats,
  useTherapistProfile,
  useTherapistAppointments,
  useUpdateAppointmentStatus,
} from '../../../hooks/useTherapistDashboard';
import { TherapistDashboardSkeleton } from '../../../components/skeletons';
import { SkipLink } from '../../../components/accessibility';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { SectionRenderer } from './SectionRenderer';
import type { TherapistDashboardSection } from '../../../types/therapist-dashboard.types';

export const TherapistDashboardPage: FC = () => {
  const { user, isLoading: authLoading, isAuthenticated, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Get active section from URL or default to overview
  const sectionParam = searchParams.get('section') as TherapistDashboardSection | null;
  const activeSection = sectionParam || 'overview';

  // Data fetching hooks
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useTherapistDashboardStats();
  const { data: profile, isLoading: profileLoading } = useTherapistProfile();
  const {
    data: appointments,
    isLoading: appointmentsLoading,
    refetch: refetchAppointments,
  } = useTherapistAppointments();
  const updateStatusMutation = useUpdateAppointmentStatus();

  // Handle section change
  const handleSectionChange = useCallback(
    (section: TherapistDashboardSection) => {
      setSearchParams({ section });
      setSidebarOpen(false);
    },
    [setSearchParams]
  );

  // Handle logout
  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Handle sidebar toggle
  const handleSidebarToggle = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Handle user menu toggle
  const handleUserMenuToggle = useCallback(() => {
    setUserMenuOpen((prev) => !prev);
  }, []);

  // Handle appointment status update
  const handleUpdateStatus = useCallback(
    (appointmentId: string, status: 'completed' | 'no_show' | 'cancelled') => {
      updateStatusMutation.mutate(
        { appointmentId, status },
        {
          onSuccess: () => {
            refetchStats();
            refetchAppointments();
          },
        }
      );
    },
    [updateStatusMutation, refetchStats, refetchAppointments]
  );

  // Get upcoming appointments (next 7 days, excluding today)
  const upcomingAppointments = useMemo(() => {
    if (!appointments) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    return appointments.filter((apt) => {
      const aptDate = new Date(apt.startTime);
      aptDate.setHours(0, 0, 0, 0);
      return aptDate > today && aptDate <= nextWeek;
    });
  }, [appointments]);

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 lg:ml-64">
        <div className="p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <TherapistDashboardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is a therapist - redirect other roles to their dashboards
  if (user.role !== 'THERAPIST') {
    // Redirect admin/super_admin to admin dashboard
    if (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') {
      return <Navigate to="/admin" replace />;
    }
    // Redirect customers to user dashboard
    return <Navigate to="/dashboard" replace />;
  }

  // Error state
  if (statsError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 p-4">
        <div className="max-w-md w-full p-8 bg-white dark:bg-slate-800 rounded-lg shadow text-center">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">Error Loading Dashboard</h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">{statsError.message}</p>
          <button
            onClick={() => refetchStats()}
            className="w-full px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      {/* Skip link for keyboard navigation */}
      <SkipLink targetId="therapist-main-content" label="Skip to dashboard content" />

      <Sidebar
        isOpen={sidebarOpen}
        activeSection={activeSection}
        onToggle={handleSidebarToggle}
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
      />

      {/* Main Content Area */}
      <div className="lg:ml-64 min-h-screen">
        <Header
          userName={user.name}
          userMenuOpen={userMenuOpen}
          onUserMenuToggle={handleUserMenuToggle}
          onSectionChange={handleSectionChange}
          onLogout={handleLogout}
        />

        {/* Page Content */}
        <main id="therapist-main-content" className="p-4 md:p-6 lg:p-8" tabIndex={-1}>
          <div className="max-w-7xl mx-auto">
            <SectionRenderer
              activeSection={activeSection}
              userName={user.name}
              stats={stats}
              profile={profile}
              statsLoading={statsLoading}
              profileLoading={profileLoading}
              appointmentsLoading={appointmentsLoading}
              upcomingAppointments={upcomingAppointments}
              onSectionChange={handleSectionChange}
              onUpdateStatus={handleUpdateStatus}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default TherapistDashboardPage;
